import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Clock, Globe, Loader2 } from 'lucide-react';
import { adminClientService, LoginHistoryResponse } from '../../services/adminClientService';
import { FullPagination } from '../../components/admin/FullPagination';

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return iso.slice(0, 16); }
}

const PAGE_SIZE = 20;

/**
 * Dettaglio Accessi (richiesta utente 23/02/2026): cronologia login del
 * cliente (data/ora + IP), raggiungibile dal menu contestuale di
 * `AdminClientsPage` e dalla card "Account Web" in `AdminClientDetailPage`.
 * Valido solo per clienti con un'utenza sul portale.
 */
export default function AdminClientAccessHistoryPage() {
  const { anConto } = useParams<{ anConto: string }>();
  const [data, setData] = useState<LoginHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    if (!anConto) return;
    setLoading(true);
    setNotFound(false);
    try {
      const res = await adminClientService.getLoginHistory(anConto, page, PAGE_SIZE);
      setData(res);
    } catch (err: any) {
      if (err?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [anConto, page]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(214,58%,41%)]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]" data-testid="client-access-history-not-found">
        <div className="text-center max-w-md">
          <p className="text-sm text-muted-foreground mb-4">Questo cliente non ha un'utenza sul portale, oppure non esiste.</p>
          <Link to={`/admin/clienti/${anConto}`} className="text-sm text-[hsl(214,58%,41%)] hover:underline">
            ← Torna alla scheda cliente
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="client-access-history-page">
      <Link
        to={`/admin/clienti/${anConto}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="client-access-history-back"
      >
        <ArrowLeft className="w-4 h-4" /> Torna alla scheda cliente
      </Link>

      <div className="rounded-xl border bg-white shadow-sm p-4 sm:p-5 mb-5">
        <h1 className="text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[hsl(214,58%,41%)]" /> Dettaglio Accessi
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data?.email}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Ultimo accesso: <span className="font-medium">{fmtDateTime(data?.lastLogin)}</span>
        </p>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        {!data || data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="client-access-history-empty">
            <Clock className="w-10 h-10 mb-3 opacity-30" />
            <p className="font-medium">Nessun accesso registrato.</p>
            <p className="text-sm mt-1 text-center max-w-sm">La cronologia login viene tracciata a partire dagli accessi successivi al 23/02/2026.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Data e ora</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Indirizzo IP</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((entry, i) => (
                <tr key={i} className="border-b last:border-b-0" data-testid={`client-access-history-row-${i}`}>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <Clock className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5 text-muted-foreground" />{fmtDateTime(entry.at)}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
                    <Globe className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />{entry.ip || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && (
        <FullPagination
          page={data.page}
          pages={data.pages}
          total={data.total}
          pageSize={data.limit}
          onPageChange={setPage}
          testIdPrefix="client-access-history-pagination"
        />
      )}
    </div>
  );
}
